import { useCallback, useEffect, useState, useMemo } from "react";

import { UploadFile } from "../../../connection/uploads";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import LargeButton from "../button/large-button";
import Icon, { COLORS, ICONS } from "../icon";
import Modal from "../modal/modal";
import WaitingIndicator from "../waiting-indicator/waiting-indicator";
import './image-upload.scss';

interface IStandardImageUpload {
	open: boolean;
	onClose: () => void;
	onSave: (image: string) => void;
	defaultImage?: string;
}

const StandardImageUpload: React.FC<IStandardImageUpload> = ({ open, onClose, onSave, defaultImage = '' }) => {
	const [imagePreview, setImagePreview] = useState('');
	const [selectedImageFile, setSelectedImageFile] = useState<any>(null);
	const [uploading, setUploading] = useState(false);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);

	useEffect(() => {
		if (open) {
			setImagePreview(defaultImage);
		}
	}, [open, defaultImage]);

	useEffect(() => {
		return () => {
			URL.revokeObjectURL(selectedImageFile);
		};
	}, [selectedImageFile]);

	const handleImageSelect = (file: File | FileList) => {
		if (selectedImageFile) {
			URL.revokeObjectURL(selectedImageFile);
		}

		if (file instanceof File) {
			setSelectedImageFile(file);
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const handleRemoveImage = () => {
		URL.revokeObjectURL(selectedImageFile);
		setImagePreview('');
		setSelectedImageFile(null);
	};

	const handleClose = useCallback(() => {
		setSelectedImageFile(null);
		setImagePreview('');
		URL.revokeObjectURL(selectedImageFile);
		onClose();
	}, [selectedImageFile, onClose]);

	const handleSave = useCallback(async () => {
		try {
			if (!user || !token) return;
			setUploading(true);
			const image = await UploadFile(user, token, selectedImageFile);
			onSave(image);
		} catch (e) {
			console.error(e);
		} finally {
			setUploading(false);
		}
	}, [onSave, user, token, selectedImageFile]);

	const footer = useMemo(() => {
		return (
			<>
				<button disabled={uploading} onClick={handleClose}>Cancel</button>
				<button disabled={uploading} onClick={handleSave} className="primary">
					{uploading ? <WaitingIndicator /> : 'Save'}
				</button>
			</>
		);
	}, [handleClose, handleSave, uploading]);

	return (
		<Modal
			className="standard-image-upload-modal"
			closeable={false}
			cancellable={true}
			title="Upload image"
			open={open}
			onRequestClose={uploading ? () => null : handleClose}
			footer={footer}
		>
			<div>
				<div className="standard-image-preview-container">
					{imagePreview ? (
						<>
							<img alt="" src={imagePreview} />
							<button
								className="no-style clear"
								onClick={handleRemoveImage}
							>
								<Icon name={ICONS.TRASH} color={COLORS.WHITE} size={16} />
							</button>
						</>
					) : (
						<LargeButton
							onFile={handleImageSelect}
							allowedFileTypes={['image/jpeg', 'image/png']}
							title="Select Image"
							subtitle="Allowed filetypes are .jpg and .png"
						/>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default StandardImageUpload;
